/**
 * Ruby AI MCP Service
 * 
 * Client service for interacting with the Ruby AI MCP Edge Function.
 * Provides Ruby with full authority profile context and control.
 * 
 * @version 1.0.0
 */

import { supabase } from "@/lib/supabaseClient";
import { buildRubyKnowledgeContext } from "../knowledge/rubyKnowledge";

// ============================================================================
// TYPES
// ============================================================================

export type MCPAction =
  | 'get_authority_overview'
  | 'get_all_issues'
  | 'get_card_issues'
  | 'get_fix_history'
  | 'get_site_profile'
  | 'mark_issue_fixed'
  | 'mark_issue_in_progress'
  | 'reset_issue_status'
  | 'add_fix_note'
  | 'bulk_mark_fixed'
  | 'request_rescore';

export interface IssueFilters {
  priority?: 'HIGH' | 'MEDIUM' | 'LOW';
  status?: 'pending' | 'fixed' | 'in_progress';
  card?: string;
}

export interface CardContext {
  cardId: string;
  cardName: string;
  score: number;
  band: string;
  issues: {
    high: number;
    medium: number;
    low: number;
    total: number;
    fixed: number;
  };
}

export interface CompositeScore {
  score: number;
  band: string;
  previousScore?: number;
  trend: 'up' | 'down' | 'stable';
  change: number;
}

export interface PrioritizedIssue {
  id: string;
  title: string;
  description: string;
  priority: 'HIGH' | 'MEDIUM' | 'LOW';
  card: string;
  cardName: string;
  route: string;
  timeEstimate: string;
  estimatedImpact?: string;
  status: 'pending' | 'fixed' | 'in_progress';
  fixedAt?: string;
  fixType: string;
  analysisType: 'technical' | 'content-gaps' | 'competitors';
  needsClarification: boolean;
  steps?: { step: number; tag: string; instruction: string }[];
  remediationHint?: string;
  currentState?: string;
  source?: 'priorityQueue' | 'savedPriorityAction';
  reportId?: string;
}

export interface AuthorityOverview {
  domain: string;
  compositeScore: CompositeScore;
  cards: Record<string, CardContext>;
  totalIssues: {
    high: number;
    medium: number;
    low: number;
    total: number;
    fixed: number;
    pending: number;
  };
  lastAuditDate: string;
  recommendedNextCard: string;
  recommendedReason: string;
  reportId?: string | null;
  scoredAt?: string;
  availableReports?: ReportSnapshot[];
  complete?: boolean;
  missingCards?: string[];
  newerIncompleteReportId?: string | null;
  reportNotice?: string | null;
  recommendations?: ReportRecommendations;
}

export interface ReportSnapshot {
  reportId: string;
  compositeScore: number | null;
  band: string | null;
  scoredAt: string | null;
  createdAt: string | null;
  complete?: boolean;
  missingCards?: string[];
}

export type ReportRecommendations =
  | {
      status: 'structured';
      reportId: string;
      actions: Array<Record<string, unknown> & { source: 'priorityQueue'; reportId: string }>;
    }
  | {
      status: 'saved_priority';
      reportId: string;
      narrative: string;
    }
  | {
      status: 'unavailable';
      reportId: string | null;
    };

export interface PriorityQueueResponse {
  priorityQueue: PrioritizedIssue[];
  summary: {
    totalPending: number;
    highImpactCount: number;
  };
}

export interface MarkFixedResponse {
  issueId: string;
  previousStatus: string;
  newStatus: string;
  fixedAt: string;
  note?: string;
  updatedCounts: {
    cardId: string;
    remainingHigh: number;
    remainingTotal: number;
    percentComplete: number;
  };
  nextRecommendedIssue?: {
    id: string;
    title: string;
    priority: string;
    card: string;
  };
}

export interface FixHistoryResponse {
  totalFixed: number;
  period: string;
  fixesByDay: Record<string, any[]>;
  recentFixes: any[];
}

export interface MCPError {
  code: string;
  message: string;
  details?: Record<string, any>;
}

export interface MCPResponse<T> {
  success: boolean;
  data?: T;
  error?: MCPError;
}

export interface McpContextData {
  overview: AuthorityOverview;
  priorityQueue: PriorityQueueResponse;
  allIssues: PrioritizedIssue[];
}

export interface McpContextOptions extends MCPReadOptions {
  queueLimit?: number;
  issuesLimit?: number;
}

// ============================================================================
// CACHE
// ============================================================================

const MCP_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const MCP_QUEUE_CACHE_LIMIT = 50;

interface MCPDomainCache {
  overview: AuthorityOverview;
  priorityQueue: PriorityQueueResponse;
  allIssues: PrioritizedIssue[];
  expiresAt: number;
}

interface MCPReadOptions {
  force?: boolean;
  reportId?: string | null;
}

// ============================================================================
// MCP CLIENT CLASS
// ============================================================================

export function formatRecommendationsForRuby(
  recommendations: ReportRecommendations | undefined,
  queue: PriorityQueueResponse
): string {
  if (recommendations?.status === 'saved_priority') {
    return `Saved priority (not a ranked action list). Preserve this wording verbatim:\n${recommendations.narrative}`;
  }

  if (recommendations?.status === 'unavailable') {
    return 'Recommendations unavailable for this selected report. Do not fill the gap from another report.';
  }

  if (queue.priorityQueue.length === 0) {
    return recommendations?.status === 'structured'
      ? 'This report’s structured actions have no pending items.'
      : 'Recommendations unavailable for this selected report. Do not fill the gap from another report.';
  }

  return queue.priorityQueue.slice(0, 10).map((issue, i) =>
    `${i + 1}. [${issue.priority}] ${issue.title}
   Card: ${issue.cardName}${issue.estimatedImpact ? ` | Impact: ${issue.estimatedImpact}` : ''}${issue.timeEstimate ? ` | Effort: ${issue.timeEstimate}` : ''}`
  ).join('\n');
}

class MCPService {
  private baseUrl: string;
  private cache = new Map<string, MCPDomainCache>();
  private inflight = new Map<string, Promise<void>>();
  /** Coalesces concurrent force-refresh calls (e.g. widget + panel after mark fixed). */
  private forcedContextRequests = new Map<string, Promise<MCPResponse<McpContextData>>>();

  constructor() {
    // Fallback to the project URL when VITE_SUPABASE_URL is not set at build time
    // (e.g. Cloudflare Pages build without the env var), matching supabaseClient.ts.
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://yqnuhxyxiyginnzvkxfm.supabase.co';
    this.baseUrl = `${supabaseUrl}/functions/v1/ruby-ai-mcp`;
  }

  /**
   * Invalidate cached MCP context for a domain (or all domains).
   * Called automatically when issue status changes.
   */
  private cacheKey(domain: string, reportId?: string | null): string {
    return `${domain}::${reportId || "latest"}`;
  }

  invalidateCache(domain?: string): void {
    if (domain) {
      for (const key of [...this.cache.keys()]) {
        if (key === domain || key.startsWith(`${domain}::`)) this.cache.delete(key);
      }
      for (const key of [...this.inflight.keys()]) {
        if (key === domain || key.startsWith(`${domain}::`)) this.inflight.delete(key);
      }
      for (const key of [...this.forcedContextRequests.keys()]) {
        if (key === domain || key.startsWith(`${domain}::`)) this.forcedContextRequests.delete(key);
      }
      return;
    }
    this.cache.clear();
    this.inflight.clear();
    this.forcedContextRequests.clear();
  }

  private getCache(domain: string, reportId?: string | null): MCPDomainCache | null {
    const key = this.cacheKey(domain, reportId);
    const entry = this.cache.get(key);
    if (!entry || Date.now() >= entry.expiresAt) {
      this.cache.delete(key);
      return null;
    }
    return entry;
  }

  private setCache(
    domain: string,
    overview: AuthorityOverview,
    priorityQueue: PriorityQueueResponse,
    allIssues: PrioritizedIssue[],
    reportId?: string | null
  ): void {
    this.cache.set(this.cacheKey(domain, reportId), {
      overview,
      priorityQueue,
      allIssues,
      expiresAt: Date.now() + MCP_CACHE_TTL_MS,
    });
  }

  private slicePriorityQueue(response: PriorityQueueResponse, limit: number): PriorityQueueResponse {
    if (limit >= response.priorityQueue.length) return response;
    return {
      ...response,
      priorityQueue: response.priorityQueue.slice(0, limit),
    };
  }

  private buildPriorityQueueFromIssues(allIssues: PrioritizedIssue[]): PriorityQueueResponse {
    const pending = allIssues.filter((i) => i.status === 'pending');
    return {
      priorityQueue: pending.slice(0, MCP_QUEUE_CACHE_LIMIT),
      summary: {
        totalPending: pending.length,
        highImpactCount: pending.filter((i) => i.priority === 'HIGH').length,
      },
    };
  }

  private withReportId(params: Record<string, any>, reportId?: string | null): Record<string, any> {
    return reportId ? { ...params, reportId } : params;
  }

  private fetchAndCacheContext(domain: string, reportId?: string | null): Promise<void> {
    return (async () => {
      // Only 2 edge calls: overview + all issues.
      // priorityQueue is derived client-side from pending issues in allIssues.
      const overviewRes = await this.request<AuthorityOverview>(
        'get_authority_overview', this.withReportId({ domain }, reportId)
      );
      if (!overviewRes.success || !overviewRes.data) return;

      // Resolve latest once. A newer delivery must not change the issue version
      // between these calls, and missing report context must never choose latest.
      const resolvedReportId = overviewRes.data.reportId;
      if (reportId && resolvedReportId !== reportId) return;
      const allIssuesRes: MCPResponse<PrioritizedIssue[]> = resolvedReportId
        ? await this.request<PrioritizedIssue[]>('get_all_issues', {
            domain, limit: 100, offset: 0, reportId: resolvedReportId,
          })
        : { success: true, data: [] };

      if (
        overviewRes.success &&
        allIssuesRes.success &&
        overviewRes.data &&
        allIssuesRes.data
      ) {
        this.setCache(
          domain,
          overviewRes.data,
          this.buildPriorityQueueFromIssues(allIssuesRes.data),
          allIssuesRes.data,
          reportId
        );
      }
    })();
  }

  /** One in-flight load per domain+report — concurrent callers share the same fetch. */
  private async ensureContext(domain: string, reportId?: string | null): Promise<MCPDomainCache> {
    const cached = this.getCache(domain, reportId);
    if (cached) return cached;

    const key = this.cacheKey(domain, reportId);
    if (!this.inflight.has(key)) {
      const load = this.fetchAndCacheContext(domain, reportId).finally(() => {
        if (this.inflight.get(key) === load) {
          this.inflight.delete(key);
        }
      });
      this.inflight.set(key, load);
    }

    await this.inflight.get(key);

    const entry = this.getCache(domain, reportId);
    if (!entry) {
      throw new Error('Failed to load MCP context');
    }
    return entry;
  }

  /**
   * Load overview + issues (2 edge calls). Priority queue is derived from pending issues.
   */
  async getMcpContext(
    domain: string,
    options?: McpContextOptions
  ): Promise<MCPResponse<McpContextData>> {
    const key = this.cacheKey(domain, options?.reportId);
    if (options?.force) {
      const pending = this.forcedContextRequests.get(key);
      if (pending) return pending;

      const request = this.loadMcpContextResponse(domain, options).finally(() => {
        if (this.forcedContextRequests.get(key) === request) {
          this.forcedContextRequests.delete(key);
        }
      });
      this.forcedContextRequests.set(key, request);
      return request;
    }

    return this.loadMcpContextResponse(domain, options);
  }

  private async loadMcpContextResponse(
    domain: string,
    options?: McpContextOptions
  ): Promise<MCPResponse<McpContextData>> {
    try {
      if (options?.force) {
        this.cache.delete(this.cacheKey(domain, options.reportId));
      }

      const entry = await this.ensureContext(domain, options?.reportId);
      const queueLimit = options?.queueLimit ?? MCP_QUEUE_CACHE_LIMIT;
      const issuesLimit = options?.issuesLimit ?? 100;

      return {
        success: true,
        data: {
          overview: entry.overview,
          priorityQueue: this.slicePriorityQueue(entry.priorityQueue, queueLimit),
          allIssues: entry.allIssues.slice(0, issuesLimit),
        },
      };
    } catch (error: any) {
      return {
        success: false,
        error: { code: 'FETCH_ERROR', message: error.message || 'Failed to load MCP context' },
      };
    }
  }

  /**
   * Make a request to the MCP Edge Function
   */
  private async request<T>(action: MCPAction, params: Record<string, any>): Promise<MCPResponse<T>> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!session) {
        return {
          success: false,
          error: { code: 'UNAUTHORIZED', message: 'User not authenticated' }
        };
      }

      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action, userId, ...params }),
      });

      const result = await response.json();
      return result;
    } catch (error: any) {
      console.error('MCP Request Error:', error);
      return {
        success: false,
        error: { code: 'NETWORK_ERROR', message: error.message }
      };
    }
  }

  // ============================================================================
  // READ OPERATIONS
  // ============================================================================

  /**
   * Get authority overview only — one edge call, does not load queue/issues unless cached.
   */
  async getAuthorityOverview(domain: string, options?: MCPReadOptions): Promise<MCPResponse<AuthorityOverview>> {
    if (!options?.force) {
      const cached = this.getCache(domain, options?.reportId);
      if (cached) return { success: true, data: cached.overview };
    }
    return this.request<AuthorityOverview>('get_authority_overview', this.withReportId({ domain }, options?.reportId));
  }

  /**
   * Get all issues across all cards with optional filters
   */
  async getAllIssues(
    domain: string,
    filters?: IssueFilters,
    limit = 50,
    offset = 0,
    options?: MCPReadOptions
  ): Promise<MCPResponse<PrioritizedIssue[]>> {
    const canUseContext =
      !filters?.priority && !filters?.status && offset === 0;

    if (canUseContext) {
      const res = await this.getMcpContext(domain, {
        ...options,
        issuesLimit: limit + offset,
      });
      if (res.success && res.data) {
        let issues = res.data.allIssues;
        if (filters?.card) issues = issues.filter((i) => i.card === filters.card);
        return { success: true, data: issues.slice(offset, offset + limit) };
      }
      if (!res.success) return { success: false, error: res.error };
    }

    return this.request<PrioritizedIssue[]>('get_all_issues', this.withReportId({
      domain,
      filters,
      limit,
      offset,
    }, options?.reportId));
  }

  /**
   * Get fix history for the past N days
   */
  async getFixHistory(domain: string, days = 30, options?: MCPReadOptions): Promise<MCPResponse<FixHistoryResponse>> {
    return this.request<FixHistoryResponse>('get_fix_history', this.withReportId({ domain, days }, options?.reportId));
  }

  // ============================================================================
  // WRITE OPERATIONS
  // ============================================================================

  /**
   * Mark an issue as fixed
   */
  async markIssueFixed(
    domain: string,
    issueId: string,
    cardId: string,
    note?: string,
    reportId?: string | null
  ): Promise<MCPResponse<MarkFixedResponse>> {
    const res = await this.request<MarkFixedResponse>(
      'mark_issue_fixed',
      this.withReportId({ domain, issueId, cardId, note }, reportId)
    );
    if (res.success) this.invalidateCache(domain);
    return res;
  }

  /**
   * Mark an issue as in progress
   */
  async markIssueInProgress(
    domain: string,
    issueId: string,
    reportId?: string | null,
    cardId?: string
  ): Promise<MCPResponse<{ issueId: string; status: string }>> {
    const res = await this.request<{ issueId: string; status: string }>(
      'mark_issue_in_progress',
      this.withReportId({ domain, issueId, cardId }, reportId)
    );
    if (res.success) this.invalidateCache(domain);
    return res;
  }

  /**
   * Reset an issue back to pending
   */
  async resetIssueStatus(
    domain: string,
    issueId: string,
    reportId?: string | null
  ): Promise<MCPResponse<{ issueId: string; status: string }>> {
    const res = await this.request<{ issueId: string; status: string }>(
      'reset_issue_status',
      this.withReportId({ domain, issueId }, reportId)
    );
    if (res.success) this.invalidateCache(domain);
    return res;
  }

  /**
   * Bulk mark multiple issues as fixed
   */
  async bulkMarkFixed(
    domain: string,
    issueIds: string[],
    note?: string,
    reportId?: string | null
  ): Promise<MCPResponse<{ bulkResults: any[]; successCount: number; failureCount: number }>> {
    const res = await this.request<{ bulkResults: any[]; successCount: number; failureCount: number }>(
      'bulk_mark_fixed',
      this.withReportId({ domain, issueIds, note }, reportId)
    );
    if (res.success) this.invalidateCache(domain);
    return res;
  }

  /**
   * Add implementation notes to a fix
   */
  async addFixNote(
    domain: string,
    issueId: string,
    note: string,
    cmsUsed?: string,
    reportId?: string | null
  ): Promise<MCPResponse<{ issueId: string; noteAdded: boolean }>> {
    return this.request('add_fix_note', this.withReportId({ domain, issueId, note, cmsUsed }, reportId));
  }

  /**
   * Request a rescore from Authority44
   */
  async requestRescore(domain: string, reason?: string): Promise<MCPResponse<{ requested: boolean; message: string }>> {
    return this.request('request_rescore', { domain, reason });
  }

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  async generateFixPrompt(issue: PrioritizedIssue, domain: string, contextBlock?: string): Promise<string> {
    const extra = contextBlock ? `\n${contextBlock}\n` : "";
    const impact = issue.estimatedImpact ? `\n**Estimated impact:** ${issue.estimatedImpact}` : '';

    // Gap 1: inject Ruby's encoded remediation knowledge (persona + output standards +
    // methodology + the expertise pack for THIS card) so fixes are card-aware and on-brand,
    // not generic SEO output. IP-safe by construction — no scoring weights/thresholds.
    const knowledge = buildRubyKnowledgeContext(issue.card);

    const sourceNote = issue.source === 'savedPriorityAction'
      ? 'savedReport.narrative.priority_action — preserve this saved recommendation verbatim'
      : 'this report’s priorityQueue';

    return `${knowledge}

=== TASK ===
Operating as Ruby (per the knowledge above), generate a fix for this flagged OG01 audit issue.

**Issue:** ${issue.title}
**Saved recommendation:** ${issue.description}
**Current state:** ${issue.currentState}
**Priority:** ${issue.priority}
**Card:** ${issue.cardName}
**Domain:** ${domain}
**Report:** ${issue.reportId || 'selected report'}
**Recommendation source:** ${sourceNote}
**Fix type:** ${issue.fixType}${impact}
${extra}
=== ACCURACY RULES ===
- Follow the priority actions from this selected report only. Do not use actions from an older report.
- Generated artifacts (FAQ schema, markup, copy) must implement THIS report’s priority actions.

Generate a complete, copy-paste ready fix following the Fix Output Standards (Mode B). Include:
1. The exact code/content/artifact to implement (copy-paste ready, using the user's real data where known)
2. Where to place it
3. A brief explanation of why this fixes the issue and its impact
4. A cross-card note only if it genuinely benefits another Smart Card

Format the output so the user can easily copy and use it. Use a code block for any code/schema/file.`;
  }

  /**
   * Build a Ruby AI prompt with full authority context
   */
  async buildRubyContext(domain: string, options?: MCPReadOptions): Promise<string> {
    const res = await this.getMcpContext(domain, { ...options, queueLimit: 15, reportId: options?.reportId });

    if (!res.success || !res.data) {
      return 'Unable to load authority context. Please try again.';
    }

    const overview = res.data.overview;
    const queue = res.data.priorityQueue;
    const recommendations = overview.recommendations;

    const aiRisk = overview.cards['ai-vulnerability'];
    const search = overview.cards['search-intelligence'];
    const brand = overview.cards['brand-insight'];
    const aiReadiness = overview.cards['ai-readiness'];
    const local = overview.cards['local-intelligence'];

    return `**User's Authority Profile:**
- Domain: ${overview.domain}
- Selected report: ${overview.reportId || 'latest'}
- Composite Score: ${overview.compositeScore.score}/100 (${overview.compositeScore.band})
- Trend: ${overview.compositeScore.trend}${overview.compositeScore.change !== 0 ? ` (${overview.compositeScore.change > 0 ? '+' : ''}${overview.compositeScore.change} points)` : ''}

**Card Breakdown:**
- AI Risk & Exposure: ${aiRisk?.score}/100 | ${aiRisk?.issues.high} HIGH, ${aiRisk?.issues.medium} MED, ${aiRisk?.issues.low} LOW | ${aiRisk?.issues.fixed} fixed
- Search Intelligence: ${search?.score}/100 | ${search?.issues.high} HIGH, ${search?.issues.medium} MED, ${search?.issues.low} LOW | ${search?.issues.fixed} fixed
  - Brand Intelligence: ${brand?.score}/100 | ${brand?.issues.high} HIGH, ${brand?.issues.medium} MED, ${brand?.issues.low} LOW | ${brand?.issues.fixed} fixed
- AI Readiness: ${aiReadiness?.score}/100 | ${aiReadiness?.issues.high} HIGH, ${aiReadiness?.issues.medium} MED, ${aiReadiness?.issues.low} LOW | ${aiReadiness?.issues.fixed} fixed
- Local Intelligence: ${local?.score}/100 | ${local?.issues.high} HIGH, ${local?.issues.medium} MED, ${local?.issues.low} LOW | ${local?.issues.fixed} fixed

**Total Issues:** ${overview.totalIssues.pending} pending (${overview.totalIssues.fixed} already fixed)

**Priority Recommendations:**
${formatRecommendationsForRuby(recommendations, queue)}

**Recommended Starting Point:** ${overview.recommendedNextCard || 'none'}
**Reason:** ${overview.recommendedReason}

**Accuracy rules:** Use only the priority actions from report ${overview.reportId || 'selected'}. Do not retrieve priority actions from an older report.

**Summary:** ${queue.summary.totalPending} ranked actions pending | ${queue.summary.highImpactCount} high priority`;
  }

  /**
   * Get a summary of the current authority state for welcome messages
   */
  async getWelcomeSummary(domain: string): Promise<{
    totalIssues: number;
    highCount: number;
    mediumCount: number;
    lowCount: number;
    fixedCount: number;
    recommendedCard: string;
    recommendedReason: string;
  } | null> {
    const res = await this.getAuthorityOverview(domain);

    if (!res.success || !res.data) {
      return null;
    }

    const overview = res.data;
    return {
      totalIssues: overview.totalIssues.total,
      highCount: overview.totalIssues.high,
      mediumCount: overview.totalIssues.medium,
      lowCount: overview.totalIssues.low,
      fixedCount: overview.totalIssues.fixed,
      recommendedCard: overview.recommendedNextCard,
      recommendedReason: overview.recommendedReason
    };
  }
}

// Export singleton instance
export const mcpService = new MCPService();

// Export class for testing
export { MCPService };
